(ns hypnos.graphql.amplify
  "Amplify wrapper"
  (:require ["aws-amplify" :as amp :refer (API Auth Hub)]
            ["axios" :as axios]
            [cljs.core.async :refer [go]]
            [cljs.core.async.interop :refer-macros [<p!]]
            [clojure.string :as string]
            [clojure.walk :as walk]
            [hypnos.config :as config]
            [hypnos.logging :as log]
            [re-frame.core :as rf]))

(defonce debug-amplify-hub-messages
  (when config/debug-amplify?
    (.listen Hub #".*" (fn [x]
                         (let [channel (.-channel x)
                               payload (.-payload x)
                               event   (.-event payload)
                               data    (.-data payload)
                               message (.-message payload)]
                           (doto js/console
                             (.groupCollapsed (string/join ": " ["Hub" channel event message]))
                             (.dir data)
                             (.groupEnd)))))))

(defn send-hub
  ([channel event]
   (send-hub channel event nil))
  ([channel event message]
   (send-hub channel event nil message))
  ([channel event data message]
   (when (and config/debug-amplify? (keyword? channel) (namespace channel))
     (.dispatch Hub (.slice (pr-str channel) 1) (clj->js {:event    event
                                                          :data     data
                                                          :message  message})))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Events

(rf/reg-event-fx
 :amplify/init
 (fn [{:keys [db]} [_ _]]
   {:db db}))

(defn query-sync
  "Query the GraphQL API synchonously."
  [query variables]
  (go
    (try
      (let [payload (<p! (.graphql API (clj->js {:query     query
                                                 :variables variables})))
            data    (walk/keywordize-keys (js->clj payload))]
        (log/debug "query-sync:" (pr-str data))
        data)
      (catch js/Error err
        (log/error "query-sync error:" (ex-cause err))
        nil))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Events for interactions with GraphQL API
;;
(rf/reg-event-fx
 :amplify/mutation
 (fn [{:keys [db] :as _cofx}
      [_ mutation variables success-event error-event]]
   (go
     (try
       (log/debug "mutation input:" (pr-str {:query     mutation
                                             :variables variables}))
       (let [payload (<p! (.graphql API
                                    (clj->js {:query     mutation
                                              :variables variables})))
             data    (walk/keywordize-keys (js->clj payload))]
         (log/debug "mutation:" (pr-str data))
         (rf/dispatch [success-event data]))
       (catch js/Error err
         (log/error "mutation error:" (ex-cause err))
         (rf/dispatch [error-event (walk/keywordize-keys (js->clj (ex-cause err)))]))))
   {:db (-> db
            (assoc :error {:type    nil
                           :message nil})
            (assoc-in [:ui :loading?] true))}))

(rf/reg-event-fx
 :amplify/mutation-api
 (fn [{:keys [db] :as _cofx}
      [_ mutation variables success-event error-event]]
   (go
     (try
       (log/debug "mutation-api input:" (pr-str {:query     mutation
                                                 :variables variables}))
       (let [payload (<p! (.graphql API
                                    (clj->js {:query     mutation
                                              :variables variables
                                              :authMode  "API_KEY"})))
             data    (walk/keywordize-keys (js->clj payload))]
         (log/debug "mutation-api:" data)
         (rf/dispatch [success-event data]))
       (catch js/Error err
         (log/error "mutation-api error:" (ex-cause err))
         (rf/dispatch [error-event (walk/keywordize-keys (js->clj (ex-cause err)))]))))
   {:db db}))

(let [lock (atom {})]
  (rf/reg-event-fx
    :amplify/query
    (fn [{:keys [db] :as _cofx}
         [rf-event query variables success-event error-event :as args]]
      (let [schema        (-> query string/trim (string/split #"\W+") second)
            acquire-lock! #(swap! lock assoc [query] [variables])
            release-lock! #(swap! lock dissoc [query])
            locked?       #(= [variables] (get @lock [query]))]
        (send-hub rf-event schema args (if (locked?) :suppressed :initiated))
        (if (not (locked?))
          (do (go
                (try
                  (acquire-lock!)
                  (let [payload (<p! (.graphql API
                                               (clj->js {:query     query
                                                         :variables variables})))
                        data    (walk/keywordize-keys (js->clj payload))]
                    (send-hub rf-event schema args (if (locked?) :resolved :discarded))
                    (when (locked?)
                      (log/debug "query:" (pr-str data))
                      (rf/dispatch [success-event data])
                      (release-lock!)))
                  (catch js/Error err
                    (send-hub rf-event schema args (if (locked?) :rejected :ignored))
                    (when (locked?)
                      (log/error "query error:" (ex-cause err))
                      (rf/dispatch [error-event (walk/keywordize-keys (js->clj (ex-cause err)))])
                      (release-lock!)))))
              {:db (-> db
                       (assoc :error {:type    nil
                                      :message nil})
                       (assoc-in [:ui :loading?] true))})
          {})))))

(rf/reg-event-fx
 :amplify/query-api
 (fn [{:keys [db] :as _cofx}
      [_ query variables success-event error-event]]
   (let [changes (atom {})]
     (go
       (try
         (log/debug "query-api input:" (pr-str {:query query :variables variables}))
         (let [payload (<p! (.graphql API
                                      (clj->js {:query     query
                                                :variables variables
                                                :authMode  "API_KEY"})))
               data    (walk/keywordize-keys (js->clj payload))]
           (log/debug "query-api:" (pr-str data))
           (rf/dispatch [success-event data]))
         (catch js/Error err
           (log/error "query-api error:" (ex-cause err))
           (rf/dispatch [error-event (walk/keywordize-keys (js->clj (ex-cause err)))]))))
     {:db (merge db @changes)})))

(rf/reg-event-fx
  :amplify/subscribe
  (fn [{:keys [db]} [_ key query variables on-success on-error]]
    (let [op      {:query query :variables variables}
          op'     (some-> db :subscriptions key (select-keys [:query :variables]))
          ok-fn   #(fn [data] (% (walk/keywordize-keys (js->clj data))))
          err-fn  #(fn [err] (% (walk/keywordize-keys (js->clj (ex-cause err)))))]
      (if (= op op')
        {}
        (as-> (.graphql API (clj->js op)) $
          (.subscribe $ (clj->js {:next (ok-fn on-success) :error (err-fn on-error)}))
          {:db (update db :subscriptions #(do (some-> % key :sub .unsubscribe)
                                              (assoc % key (assoc op :sub $))))})))))

(rf/reg-event-fx
  :amplify/unsubscribe
  (fn [{:keys [db]} [_ key]]
    (some-> db :subscriptions key :sub .unsubscribe)
    {:db (update db :subscriptions dissoc key)}))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Events for interactions with REST API
;;

(rf/reg-event-fx
 :amplify/get
 (fn [{:keys [db] :as _cofx}
      [_ api path params callback-event]]
   (go
     (try
       (let [payload (<p! (.get API api path (clj->js params)))
             data    (walk/keywordize-keys (js->clj payload))]
         (log/debug "get:" (pr-str data))
         (rf/dispatch [callback-event data]))
       (catch js/Error err
         (js/console.log (ex-cause err)))))
   {:db db}))

(rf/reg-event-fx
 :amplify/upload-file
 (fn [{:keys [db] :as _cofx}
      [_ _api _path callback-event]]
   (go
     (loop [epoch 1]
       (let [form-data     (doto (js/FormData.)
                             (.append "consult-key" (get-in db [:consult :consult/key]))
                             (.append "portrait" (get-in db [:portrait :file])))
             params        {:headers {"Cache-Control"   "no-cache"
                                      "Referrer-Policy" "unsafe-url"}
                            :timeout 300000}
             [success val] (try
                             [true (<p! (.post axios
                                               (str config/REST-URL "/p")
                                               form-data
                                               (clj->js params)))]
                             (catch js/Error e
                               [false e]))]
         (if success
           (rf/dispatch [callback-event {:data (walk/keywordize-keys (js->clj (.-data val)))}])
           (if (<= epoch 5)
             (recur (inc epoch))
             (do
               (rf/dispatch [:consumer/set-error {:type :too-many-retries
                                                  :key  :portrait-upload-error}])
               (rf/dispatch [:consumer/set-portrait-upload-status :failed])))))))
   {:db db}))

;; TODO
(rf/reg-event-fx
 :amplify/upload-file-by-therapist
 (fn [{:keys [db] :as _cofx}
      [_ consult-key callback-event]]
   (go
     (loop [epoch 1]
       (let [form-data     (doto (js/FormData.)
                             (.append "consult-key" consult-key)
                             (.append "portrait" (get-in db [:portrait :file])))
             params        {:headers {"Cache-Control"   "no-cache"
                                      "Referrer-Policy" "unsafe-url"}
                            :timeout 300000}
             [success val] (try
                             [true (<p! (.post axios
                                               (str config/REST-URL "/p")
                                               form-data
                                               (clj->js params)))]
                             (catch js/Error e
                               [false e]))]
         (if success
           (rf/dispatch [callback-event {:data (walk/keywordize-keys (js->clj (.-data val)))}])
           (if (<= epoch 5)
             (recur (inc epoch))
             (do
               (rf/dispatch [:backoffice/set-error {:type :too-many-retries
                                                    :key  :portrait-upload-error}])
               (rf/dispatch [:portrait/failed])))))))
   {:db db}))

(let [lock (atom {})]
  (rf/reg-event-fx
    :amplify/download-file
    (fn [{:keys [_db] :as _cofx}
         [rf-event  {:keys [id]} callback-event :as args]]
      (let [acquire-lock! #(reset! lock [id])
            release-lock! #(reset! lock nil)
            locked?       #(= [id] @lock)]
        (send-hub rf-event id args (if (locked?) :suppressed :initiated))
        (when (not (locked?))
          (go
            (try
              (acquire-lock!)
              (let [session (<p! (.currentSession Auth))
                    token   (.getJwtToken (.getIdToken session))
                    params  {:method       "get"
                             :url          (str config/REST-URL "/p/" id)
                             :headers      {"Access-Control-Allow-Origin" "*"
                                            "AuthToken"                   token
                                            ;; "Referrer-Policy"             "unsafe-url"
                                            }
                             :responseType "blob"
                             :timeout      300000}
                    payload (<p! (axios (clj->js params)))
                    data    (.-data payload)]
                (send-hub rf-event id args (if (locked?) :resolved :discarded))
                (when (locked?)
                  (rf/dispatch [callback-event data])
                  (release-lock!)))
              (catch js/Error err
                (send-hub rf-event id args (if (locked?) :rejected :ignored))
                (when (locked?)
                  (js/console.log (ex-cause err))
                  (release-lock!))))))
        {}))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Authentication
;;

(rf/reg-event-fx
 :amplify/get-auth-session
 (fn [{:keys [db] :as _cofx}
      [_ callback-event]]
   (go
     (try
       (let [session (<p! (.currentSession Auth))
             payload (walk/keywordize-keys (js->clj (.-payload (.-idToken session))))]
         (log/debug "get-auth-session:" payload)
         (rf/dispatch [callback-event {:groups (set (:cognito:groups payload))}]))
       (catch js/Error err
         (js/console.log (ex-cause err))
         (rf/dispatch [callback-event {:groups #{}}]))))
   {:db db}))
